import React, { useState } from 'react';
import type { InsuranceProviderPlanDetailViewModel, BillingRequirementViewModel } from '@generated';
import { editPlanBillingRequirementEnforcementOptions, getInsuranceProviderPlanDetailOptions } from '@generated';
import { useMutation } from '~/util/http/useMutation';

interface BillingRequirementsTabProps {
	plan: InsuranceProviderPlanDetailViewModel;
}

const enforcementLevelOptions = [
	{ id: 1, label: 'Required', color: 'bg-red-100 text-red-700' },
	{ id: 2, label: 'Warning', color: 'bg-yellow-100 text-yellow-700' },
	{ id: 3, label: 'Ignore', color: 'bg-gray-100 text-gray-500' }
];

const categoryOrder = ['Documentation', 'Clinical', 'Authorization', 'Timing', 'Visits', 'Financial', 'Codes', 'Eligibility'];

export function BillingRequirementsTab({ plan }: BillingRequirementsTabProps) {
	const [filterCategory, setFilterCategory] = useState<string>('');
	const [filterEnforcement, setFilterEnforcement] = useState<number | ''>('');

	const updateMutation = useMutation({
		...editPlanBillingRequirementEnforcementOptions,
		invalidateQueryKeys: [getInsuranceProviderPlanDetailOptions(plan.id).queryKey]
	});

	// Group by category
	const grouped = plan.billingRequirements.reduce((acc, req) => {
		if (!acc[req.category]) acc[req.category] = [];
		acc[req.category].push(req);
		return acc;
	}, {} as Record<string, BillingRequirementViewModel[]>);

	const sortedCategories = categoryOrder.filter(c => grouped[c]);

	const handleEnforcementChange = async (req: BillingRequirementViewModel, enforcementLevelId: number) => {
		await updateMutation.mutateAsync({
			planId: plan.id,
			billingRequirementTypeId: req.billingRequirementTypeId,
			enforcementLevelId
		});
	};

	return (
		<div className="mt-6 space-y-4">
			{/* Filters */}
			<div className="flex gap-4">
				<select
					value={filterCategory}
					onChange={e => setFilterCategory(e.target.value)}
					className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
				>
					<option value="">All Categories</option>
					{sortedCategories.map(cat => (
						<option key={cat} value={cat}>
							{cat}
						</option>
					))}
				</select>
				<select
					value={filterEnforcement}
					onChange={e => setFilterEnforcement(e.target.value ? Number(e.target.value) : '')}
					className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
				>
					<option value="">All Levels</option>
					{enforcementLevelOptions.map(opt => (
						<option key={opt.id} value={opt.id}>
							{opt.label}
						</option>
					))}
				</select>
			</div>

			{sortedCategories
				.filter(cat => !filterCategory || cat === filterCategory)
				.map(category => {
					const requirements = grouped[category].filter(r => !filterEnforcement || r.enforcementLevelId === filterEnforcement);
					if (requirements.length === 0) return null;

					return (
						<div key={category} className="rounded-xl border border-gray-200 bg-white shadow-sm">
							<div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
								<h4 className="text-sm font-semibold text-gray-700">
									{category} <span className="text-gray-400">({requirements.length})</span>
								</h4>
							</div>
							<div className="divide-y divide-gray-100">
								{requirements.map(req => (
									<div key={req.id} className="flex items-center justify-between px-4 py-3">
										<div className="flex-1">
											<p className="text-sm font-medium text-gray-900">{req.billingRequirementTypeLabel}</p>
											<div className="mt-1 flex gap-1">
												{req.appliesToPdgm && (
													<span className="rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-600">PDGM</span>
												)}
												{req.appliesToPps && (
													<span className="rounded bg-purple-50 px-1.5 py-0.5 text-xs text-purple-600">PPS</span>
												)}
												{req.appliesToFfs && (
													<span className="rounded bg-green-50 px-1.5 py-0.5 text-xs text-green-600">FFS</span>
												)}
											</div>
										</div>
										<select
											value={req.enforcementLevelId}
											onChange={e => handleEnforcementChange(req, Number(e.target.value))}
											className={`rounded-lg border border-gray-300 px-3 py-1.5 text-sm ${
												enforcementLevelOptions.find(o => o.id === req.enforcementLevelId)?.color ?? ''
											}`}
										>
											{enforcementLevelOptions.map(opt => (
												<option key={opt.id} value={opt.id}>
													{opt.label}
												</option>
											))}
										</select>
									</div>
								))}
							</div>
						</div>
					);
				})}
		</div>
	);
}
